import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { FileText, X, Download, Lock, ArrowRight } from 'lucide-react';

const getBaseURL = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.startsWith('http')) {
    return envUrl.replace(/\/$/, ''); // usuń trailing slash
  }
  return '';
};

const PublicInvoicePreview = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [htmlContent, setHtmlContent] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const pdfUrlRef = useRef(null);
  const attachmentUrlRef = useRef(null);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!token) {
        setError('Brak tokenu dostępu');
        setLoading(false);
        return;
      }

      const baseURL = getBaseURL();
      const apiUrl = `${baseURL}/api/invoices/${id}/public-preview?token=${encodeURIComponent(token)}`;
      const fileUrl = `${baseURL}/api/invoices/${id}/public-file?token=${encodeURIComponent(token)}`;

      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(apiUrl, { responseType: 'text' });
        setHtmlContent(response.data);
        setPdfUrl(null);
        try {
          const attUrl = `${baseURL}/api/invoices/${id}/public-attachments?token=${encodeURIComponent(token)}`;
          const attRes = await axios.get(attUrl);
          setAttachments(Array.isArray(attRes.data) ? attRes.data : []);
        } catch (_) {
          setAttachments([]);
        }
        try {
          const sigUrl = `${baseURL}/api/invoices/${id}/public-signatures-and-descriptions?token=${encodeURIComponent(token)}`;
          const sigRes = await axios.get(sigUrl);
          setSignatures(Array.isArray(sigRes.data?.signatures) ? sigRes.data.signatures : []);
          setDescriptions(Array.isArray(sigRes.data?.descriptions) ? sigRes.data.descriptions : []);
        } catch (_) {
          setSignatures([]);
          setDescriptions([]);
        }
      } catch (err) {
        if (err.response?.status === 403) {
          setError('Nieprawidłowy token dostępu');
          setLoading(false);
          return;
        }
        if (err.response?.status === 404) {
          setError('Faktura nie została znaleziona');
          setLoading(false);
          return;
        }
        if (err.response?.status === 400) {
          try {
            const fileResponse = await axios.get(fileUrl, { responseType: 'blob' });
            const blob = new Blob([fileResponse.data], {
              type: fileResponse.headers['content-type'] || 'application/pdf',
            });
            const url = URL.createObjectURL(blob);
            if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = url;
            setPdfUrl(url);
            setHtmlContent('');
            try {
              const attUrl = `${baseURL}/api/invoices/${id}/public-attachments?token=${encodeURIComponent(token)}`;
              const attRes = await axios.get(attUrl);
              setAttachments(Array.isArray(attRes.data) ? attRes.data : []);
            } catch (_) {
              setAttachments([]);
            }
            try {
              const sigUrl = `${baseURL}/api/invoices/${id}/public-signatures-and-descriptions?token=${encodeURIComponent(token)}`;
              const sigRes = await axios.get(sigUrl);
              setSignatures(Array.isArray(sigRes.data?.signatures) ? sigRes.data.signatures : []);
              setDescriptions(Array.isArray(sigRes.data?.descriptions) ? sigRes.data.descriptions : []);
            } catch (_) {
              setSignatures([]);
              setDescriptions([]);
            }
          } catch (fileErr) {
            console.error('Error fetching invoice file:', fileErr);
            setError('Błąd podczas pobierania podglądu faktury');
          }
          setLoading(false);
          return;
        }
        try {
          const fileResponse = await axios.get(fileUrl, { responseType: 'blob' });
          const blob = new Blob([fileResponse.data], {
            type: fileResponse.headers['content-type'] || 'application/pdf',
          });
          const url = URL.createObjectURL(blob);
          if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
          pdfUrlRef.current = url;
          setPdfUrl(url);
          setHtmlContent('');
          try {
            const attUrl = `${baseURL}/api/invoices/${id}/public-attachments?token=${encodeURIComponent(token)}`;
            const attRes = await axios.get(attUrl);
            setAttachments(Array.isArray(attRes.data) ? attRes.data : []);
          } catch (_) {
            setAttachments([]);
          }
          try {
            const sigUrl = `${baseURL}/api/invoices/${id}/public-signatures-and-descriptions?token=${encodeURIComponent(token)}`;
            const sigRes = await axios.get(sigUrl);
            setSignatures(Array.isArray(sigRes.data?.signatures) ? sigRes.data.signatures : []);
            setDescriptions(Array.isArray(sigRes.data?.descriptions) ? sigRes.data.descriptions : []);
          } catch (_) {
            setSignatures([]);
            setDescriptions([]);
          }
        } catch (fileErr) {
          console.error('Error fetching invoice preview:', err);
          setError('Błąd podczas pobierania podglądu faktury');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id && token) {
      fetchPreview();
    } else if (id && !token) {
      setError('Brak tokenu dostępu w adresie URL');
      setLoading(false);
    }

    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
      if (attachmentUrlRef.current) {
        URL.revokeObjectURL(attachmentUrlRef.current);
        attachmentUrlRef.current = null;
      }
    };
  }, [id, token]);

  const handleAttachmentClick = async (attachment) => {
    setLoadingAttachment(true);
    setSelectedAttachment(attachment);
    setAttachmentPreviewUrl(null);
    try {
      const baseURL = getBaseURL();
      const url = `${baseURL}/api/invoices/${id}/attachments/${attachment.id}/public-download?token=${encodeURIComponent(token)}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = URL.createObjectURL(blob);
      if (attachmentUrlRef.current) URL.revokeObjectURL(attachmentUrlRef.current);
      attachmentUrlRef.current = blobUrl;
      setAttachmentPreviewUrl(blobUrl);
    } catch (err) {
      console.error('Error fetching attachment:', err);
      setSelectedAttachment(null);
    } finally {
      setLoadingAttachment(false);
    }
  };

  const handleCloseAttachment = () => {
    setSelectedAttachment(null);
    setAttachmentPreviewUrl(null);
    if (attachmentUrlRef.current) {
      URL.revokeObjectURL(attachmentUrlRef.current);
      attachmentUrlRef.current = null;
    }
  };

  const handleDownloadAttachment = () => {
    if (!selectedAttachment || !attachmentPreviewUrl) return;
    const a = document.createElement('a');
    a.href = attachmentPreviewUrl;
    a.download = selectedAttachment.originalFileName || `zalacznik-${selectedAttachment.id}`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Błąd</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const isPreviewableType = (att) => {
    const ft = att?.fileType || '';
    const fn = (att?.originalFileName || '').toLowerCase();
    return ft.includes('pdf') || fn.endsWith('.pdf') ||
      ft.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(fn);
  };

  const renderAttachmentsBar = () => {
    if (!attachments || attachments.length === 0) return null;
    return (
      <div className="mb-3">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Załączniki</h3>
        <div className="flex flex-wrap gap-2">
          {attachments.map((att) => (
            <button
              key={att.id}
              onClick={() => handleAttachmentClick(att)}
              className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-700 bg-white hover:bg-gray-100 rounded border border-gray-300 whitespace-nowrap shadow-sm"
              title={att.originalFileName}
            >
              <FileText className="h-3 w-3 flex-shrink-0" />
              <span className="truncate max-w-[140px]">{att.originalFileName}</span>
              {att.source === 'KSEF' && (
                <span className="flex-shrink-0 px-1 py-0.5 text-[9px] font-medium text-amber-800 bg-amber-100 rounded">KSeF</span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderAttachmentModal = () => {
    if (!selectedAttachment) return null;
    const canPreview = isPreviewableType(selectedAttachment);
    const fileType = selectedAttachment.fileType || '';
    const isPdf = fileType.includes('pdf') || (selectedAttachment.originalFileName || '').toLowerCase().endsWith('.pdf');
    const isImage = fileType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(selectedAttachment.originalFileName || '');
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={handleCloseAttachment}>
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between p-3 border-b">
            <span className="font-medium text-gray-900 truncate flex-1">{selectedAttachment.originalFileName}</span>
            <div className="flex gap-2">
              <button
                onClick={handleDownloadAttachment}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Pobierz"
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                onClick={handleCloseAttachment}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Zamknij"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4 bg-gray-50 min-h-[400px]">
            {loadingAttachment ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
              </div>
            ) : attachmentPreviewUrl ? (
              canPreview ? (
                isImage ? (
                  <img src={attachmentPreviewUrl} alt={selectedAttachment.originalFileName} className="max-w-full h-auto mx-auto" />
                ) : isPdf ? (
                  <iframe src={attachmentPreviewUrl} title="Podgląd" className="w-full border-0" style={{ minHeight: '60vh' }} />
                ) : (
                  <div className="flex flex-col items-center justify-center p-8">
                    <FileText className="h-16 w-16 text-gray-300 mb-4" />
                    <p className="text-gray-600 mb-4">Ten typ pliku nie może być wyświetlony w przeglądarce.</p>
                    <button onClick={handleDownloadAttachment} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2">
                      <Download className="h-4 w-4" /> Pobierz plik
                    </button>
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center p-8">
                  <FileText className="h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-gray-600 mb-4">Ten typ pliku nie może być wyświetlony w przeglądarce.</p>
                  <button onClick={handleDownloadAttachment} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2">
                    <Download className="h-4 w-4" /> Pobierz plik
                  </button>
                </div>
              )
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderSignaturesSection = () => {
    if (!signatures || signatures.length === 0) return null;
    const uniqueSignatures = new Map();
    signatures.forEach(sig => {
      if (sig.user && sig.signedAt) {
        const key = sig.signatureGroupId || `${sig.user.id}_${new Date(sig.signedAt).toISOString().substring(0, 19)}`;
        if (!uniqueSignatures.has(key)) uniqueSignatures.set(key, sig);
      }
    });
    const sortedSignatures = Array.from(uniqueSignatures.values()).sort(
      (a, b) => new Date(a.signedAt) - new Date(b.signedAt)
    );
    const formatCompactDate = (date) => {
      const d = new Date(date);
      return `${d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit' })}, ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
    };
    return (
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
          <Lock className="h-5 w-5 text-gray-600" />
          <h2 className="text-base font-semibold text-gray-900">Podpisy elektroniczne – historia podpisów</h2>
        </div>
        <div className="p-4 overflow-x-auto">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {sortedSignatures.map((sig, idx) => {
              let description = descriptions.find(d => d.documentSignatureId && String(d.documentSignatureId) === String(sig.id));
              if (!description && sig.user?.id) {
                description = descriptions.find(d => {
                  if (!d.userId || String(d.userId) !== String(sig.user.id)) return false;
                  if (d.documentSignatureId && String(d.documentSignatureId) !== String(sig.id)) return false;
                  const timeDiff = Math.abs(new Date(d.createdAt || d.updatedAt || 0) - new Date(sig.signedAt));
                  return timeDiff < 900000;
                });
              }
              if (!description && sig.user?.id) {
                const userDescriptions = descriptions.filter(d =>
                  d.userId && String(d.userId) === String(sig.user.id) && (!d.documentSignatureId || d.documentSignatureId === sig.id)
                );
                if (userDescriptions.length > 0) {
                  const sigTime = new Date(sig.signedAt).getTime();
                  let closest = null;
                  let minDiff = Infinity;
                  userDescriptions.forEach(d => {
                    const diff = Math.abs(new Date(d.createdAt || d.updatedAt || 0).getTime() - sigTime);
                    if (diff < 3600000 && diff < minDiff) {
                      minDiff = diff;
                      closest = d;
                    }
                  });
                  if (closest) description = closest;
                }
              }
              const descToShow = description;
              const isAttachmentSignature = sig.attachmentId != null;
              const isRealizacjiOnly = descToShow && descToShow.zatwierdzonoDo === 'REALIZACJI' && !descToShow.metricalChecked && !descToShow.preliminaryCheckDone && !descToShow.formalnoRachunkowoChecked && !isAttachmentSignature;
              const bgColor = isAttachmentSignature ? 'bg-blue-50' : (isRealizacjiOnly ? 'bg-emerald-50' : 'bg-green-50');
              const borderColor = isAttachmentSignature ? 'border-blue-200' : (isRealizacjiOnly ? 'border-emerald-300' : 'border-green-200');
              const iconColor = isAttachmentSignature ? 'text-blue-600' : (isRealizacjiOnly ? 'text-emerald-600' : 'text-green-600');
              const arrowColor = isAttachmentSignature ? 'text-blue-600' : (isRealizacjiOnly ? 'text-emerald-600' : 'text-green-600');
              const userName = sig.user ? `${sig.user.firstName || ''} ${sig.user.lastName || ''}`.trim() || sig.user.email || 'Nieznany' : 'Nieznany';
              const dateStr = formatCompactDate(sig.signedAt);
              return (
                <React.Fragment key={sig.signatureGroupId || sig.id || idx}>
                  <div className={`${bgColor} border ${borderColor} rounded-md p-2.5 flex-shrink-0 min-w-[160px] sm:min-w-[180px] max-w-[220px]`}>
                    <div className="flex items-start gap-1.5">
                      <Lock className={`h-3.5 w-3.5 ${iconColor} mt-0.5 flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        {descToShow && descToShow.metricalChecked ? (
                          <>
                            <div className="font-semibold text-green-700 text-xs">SPRAWDZONO MERYTORYCZNYM</div>
                            <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                            <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                          </>
                        ) : descToShow && descToShow.preliminaryCheckDone ? (
                          <>
                            <div className="font-semibold text-blue-700 text-xs">WSTĘPNA KONTROLA</div>
                            <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                            <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                          </>
                        ) : descToShow && descToShow.formalnoRachunkowoChecked ? (
                          <>
                            <div className="font-semibold text-purple-700 text-xs">SPRAWDZONO FORMALNO-RACHUNKOWO</div>
                            <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                            <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                            {descToShow.zatwierdzonoDo && descToShow.zatwierdzonoDo !== 'REALIZACJI' && (
                              <div className="mt-1 pt-1 border-t border-green-300 text-[10px]">DO: <strong className="text-purple-700">{descToShow.zatwierdzonoDo}</strong></div>
                            )}
                            {descToShow.kwota && (
                              <div className="text-[10px] text-gray-700 mt-0.5"><strong>{descToShow.kwota} PLN</strong></div>
                            )}
                          </>
                        ) : descToShow && descToShow.zatwierdzonoDo === 'REALIZACJI' ? (
                          <>
                            <div className="font-semibold text-red-700 text-xs">ZATWIERDZONO DO REALIZACJI</div>
                            <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                            <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                          </>
                        ) : isAttachmentSignature ? (
                          <>
                            <div className="font-semibold text-blue-700 text-xs">ZAŁĄCZNIK</div>
                            <div className="text-gray-600 text-xs mt-0.5 truncate" title={sig.attachmentFileName}>{sig.attachmentFileName || 'załącznik'}</div>
                            <div className="text-gray-600 text-[10px] mt-0.5">{userName}</div>
                            <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                          </>
                        ) : (
                          <>
                            <div className="font-semibold text-gray-700 text-xs">PODPISANO</div>
                            <div className="text-gray-600 text-xs mt-0.5">{userName}</div>
                            <div className="text-gray-500 text-[10px] mt-0.5">{dateStr}</div>
                          </>
                        )}
                        {!isAttachmentSignature && descToShow && descToShow.invoicePaymentType && (
                          <div className="mt-1 pt-1 border-t border-gray-300 text-[10px] text-gray-700">
                            <span className="font-medium">Typ płatności: </span>
                            {descToShow.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : descToShow.invoicePaymentType === 'TRANSFER' ? 'Przelew' : descToShow.invoicePaymentType}
                          </div>
                        )}
                        {!isAttachmentSignature && descToShow && descToShow.description && (
                          <div className="mt-1.5 pt-1.5 border-t border-gray-300">
                            <div className="text-[10px] text-gray-700 truncate" title={descToShow.description}>
                              {descToShow.description?.length > 45 ? `${descToShow.description.substring(0, 45)}…` : descToShow.description}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {idx < sortedSignatures.length - 1 && (
                    <div className="flex items-center justify-center flex-shrink-0 px-0.5">
                      <ArrowRight className={`h-4 w-4 ${arrowColor}`} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (pdfUrl) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-4">
          {renderAttachmentsBar()}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <embed
              src={pdfUrl}
              type="application/pdf"
              className="w-full border-0"
              style={{ minHeight: '60vh' }}
            />
          </div>
          {renderSignaturesSection()}
        </div>
        {renderAttachmentModal()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4">
        {renderAttachmentsBar()}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <iframe
            title="Invoice Preview"
            srcDoc={htmlContent}
            className="w-full border-0"
            style={{ minHeight: '60vh' }}
          />
        </div>
        {renderSignaturesSection()}
      </div>
      {renderAttachmentModal()}
    </div>
  );
};

export default PublicInvoicePreview;
